import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';

const SOLUTION_KEYS = [
  'tracking',
  'budgets',
  'goals',
  'couple',
  'chat',
  'family',
  'credits',
  'scan',
  'business',
  'properties',
  'projects',
];

function MockupTracking() {
  const allItems = [
    { icon: '🍽️', name: 'Supermercado', amount: '-$85,500', color: 'text-red-400', bg: 'bg-amber-100 dark:bg-amber-500/20' },
    { icon: '🚗', name: 'Uber', amount: '-$12,000', color: 'text-red-400', bg: 'bg-blue-100 dark:bg-blue-500/20' },
    { icon: '💰', name: 'Salario', amount: '+$3,500,000', color: 'text-emerald-500', bg: 'bg-emerald-100 dark:bg-emerald-500/20' },
    { icon: '💊', name: 'Farmacia', amount: '-$32,000', color: 'text-red-400', bg: 'bg-teal-100 dark:bg-teal-500/20' },
    { icon: '📚', name: 'Curso online', amount: '-$29,990', color: 'text-red-400', bg: 'bg-cyan-100 dark:bg-cyan-500/20' },
  ];

  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all'
    ? allItems.slice(0, 3)
    : filter === 'expense'
      ? allItems.filter((i) => i.color === 'text-red-400').slice(0, 3)
      : allItems.filter((i) => i.color === 'text-emerald-500');

  return (
    <div className="mt-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3 select-none">
      <div className="flex gap-1 mb-2.5">
        {[
          { key: 'all', label: 'Todos' },
          { key: 'expense', label: 'Gastos' },
          { key: 'income', label: 'Ingresos' },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`text-[9px] font-semibold px-2 py-0.5 rounded-full transition-all ${
              filter === f.key
                ? 'bg-brand-500 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {filtered.map((item) => (
          <div key={item.name} className="flex items-center gap-2 rounded-lg p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors cursor-default">
            <div className={`w-7 h-7 rounded-lg ${item.bg} flex items-center justify-center text-xs`}>
              {item.icon}
            </div>
            <span className="flex-1 text-[11px] font-medium text-gray-700 dark:text-gray-300 truncate">
              {item.name}
            </span>
            <span className={`text-[11px] font-bold ${item.color}`}>{item.amount}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockupBudgets() {
  const [budgets, setBudgets] = useState([
    { name: 'Comida', spent: 260, limit: 400, color: 'bg-amber-400' },
    { name: 'Transporte', spent: 67, limit: 150, color: 'bg-blue-400' },
    { name: 'Entretencion', spent: 90, limit: 100, color: 'bg-pink-400' },
  ]);

  const addSpend = (i) => {
    setBudgets((prev) =>
      prev.map((b, idx) =>
        idx === i ? { ...b, spent: Math.min(b.spent + 20, b.limit + 30) } : b
      )
    );
  };

  return (
    <div className="mt-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3 space-y-2.5 select-none">
      {budgets.map((item, i) => {
        const pct = Math.min(Math.round((item.spent / item.limit) * 100), 100);
        const over = item.spent > item.limit;
        return (
          <div
            key={item.name}
            onClick={() => addSpend(i)}
            className="cursor-pointer group"
            title="Click para simular gasto"
          >
            <div className="flex justify-between text-[10px] mb-1">
              <span className="text-gray-600 dark:text-gray-400">{item.name}</span>
              <span className={`font-bold transition-colors ${over ? 'text-red-500' : pct >= 80 ? 'text-amber-500' : 'text-gray-500 dark:text-gray-400'}`}>
                {over ? 'Excedido!' : `${pct}%`}
              </span>
            </div>
            <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${over ? 'bg-red-500' : item.color}`}
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
            <div className="text-[9px] text-gray-400 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              ${item.spent.toLocaleString()} / ${item.limit.toLocaleString()}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MockupGoals() {
  const [goals, setGoals] = useState([
    { icon: '✈️', name: 'Vacaciones', saved: 800, target: 2000 },
    { icon: '💻', name: 'Laptop', saved: 1500, target: 1500 },
  ]);

  const addFunds = (i) => {
    setGoals((prev) =>
      prev.map((g, idx) =>
        idx === i ? { ...g, saved: Math.min(g.saved + 200, g.target) } : g
      )
    );
  };

  return (
    <div className="mt-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3 space-y-2.5 select-none">
      {goals.map((item, i) => {
        const pct = Math.round((item.saved / item.target) * 100);
        const done = item.saved >= item.target;
        return (
          <div
            key={item.name}
            onClick={() => !done && addFunds(i)}
            className={`${!done ? 'cursor-pointer' : ''}`}
            title={!done ? 'Click para ahorrar' : ''}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-sm">{item.icon}</span>
              <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">{item.name}</span>
              <span className={`ml-auto text-[10px] font-semibold ${done ? 'text-emerald-500' : 'text-gray-400'}`}>
                {done ? 'Completada!' : `${pct}%`}
              </span>
            </div>
            <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${done ? 'bg-emerald-400' : 'bg-gradient-to-r from-brand-500 to-accent-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MockupCouple() {
  const [splitType, setSplitType] = useState('equal'); // equal | 70-30 | custom

  const splits = {
    equal: { me: '$32,500', partner: '$32,500', label: '50/50' },
    '70-30': { me: '$45,500', partner: '$19,500', label: '70/30' },
    custom: { me: '$40,000', partner: '$25,000', label: 'Custom' },
  };
  const current = splits[splitType];

  return (
    <div className="mt-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3 select-none">
      <div className="flex gap-1 mb-2.5 justify-center">
        {Object.keys(splits).map((key) => (
          <button
            key={key}
            onClick={() => setSplitType(key)}
            className={`text-[9px] font-semibold px-2 py-0.5 rounded-full transition-all ${
              splitType === key
                ? 'bg-pink-500 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {splits[key].label}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-[9px] font-bold text-white">FR</div>
          <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400">{current.me}</span>
        </div>
        <div className="text-[10px] font-bold text-pink-400">{current.label}</div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400">{current.partner}</span>
          <div className="w-6 h-6 rounded-full bg-pink-500 flex items-center justify-center text-[9px] font-bold text-white">AN</div>
        </div>
      </div>
      <div className="text-center">
        <div className="text-[10px] text-gray-400 mb-0.5">Cena restaurante</div>
        <div className="text-xs font-bold text-gray-700 dark:text-gray-300">$65,000</div>
      </div>
    </div>
  );
}

function MockupChat() {
  const conversations = [
    { q: 'Cuanto gaste este mes?', a: 'Gastos totales: $1,475,490' },
    { q: 'Cual es mi balance?', a: 'Balance: $2,924,510' },
    { q: 'Cuanto gaste en comida?', a: 'Alimentacion: $390,500' },
  ];

  const [chatIndex, setChatIndex] = useState(0);
  const [typing, setTyping] = useState(false);

  const next = () => {
    if (typing) return;
    setTyping(true);
    setTimeout(() => {
      setChatIndex((prev) => (prev + 1) % conversations.length);
      setTyping(false);
    }, 600);
  };

  const conv = conversations[chatIndex];

  return (
    <div className="mt-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3 space-y-2 select-none">
      <div className="flex justify-end">
        <div className="bg-brand-500 text-white text-[10px] px-2.5 py-1.5 rounded-xl rounded-br-sm max-w-[85%]">
          {conv.q}
        </div>
      </div>
      <div className="flex justify-start">
        <div className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-[10px] px-2.5 py-1.5 rounded-xl rounded-bl-sm max-w-[85%]">
          {typing ? (
            <span className="inline-flex gap-0.5">
              <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </span>
          ) : (
            conv.a
          )}
        </div>
      </div>
      <div className="flex gap-1 flex-wrap">
        {conversations
          .filter((_, i) => i !== chatIndex)
          .slice(0, 2)
          .map((c) => (
            <button
              key={c.q}
              onClick={next}
              className="text-[9px] px-2 py-1 rounded-full bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 hover:bg-brand-100 dark:hover:bg-brand-500/20 transition-colors"
            >
              {c.q.slice(0, 20)}...
            </button>
          ))}
      </div>
    </div>
  );
}

function MockupFamily() {
  const [selected, setSelected] = useState(null);

  const members = [
    { initials: 'FR', name: 'Francisco', amount: 820000, color: 'bg-blue-500', categories: [{ name: 'Comida', val: '$390k' }, { name: 'Transporte', val: '$180k' }] },
    { initials: 'AN', name: 'Ana', amount: 345000, color: 'bg-pink-500', categories: [{ name: 'Shopping', val: '$200k' }, { name: 'Salud', val: '$85k' }] },
    { initials: 'SO', name: 'Sofia', amount: 180000, color: 'bg-purple-500', categories: [{ name: 'Educacion', val: '$120k' }, { name: 'Comida', val: '$60k' }] },
  ];

  return (
    <div className="mt-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3 space-y-2 select-none">
      {members.map((m, i) => (
        <div key={m.initials}>
          <div
            onClick={() => setSelected(selected === i ? null : i)}
            className="flex items-center gap-2 cursor-pointer rounded-lg p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
          >
            <div className={`w-6 h-6 rounded-full ${m.color} flex items-center justify-center text-[9px] font-bold text-white`}>
              {m.initials}
            </div>
            <span className="flex-1 text-[11px] font-medium text-gray-700 dark:text-gray-300">{m.name}</span>
            <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400">${(m.amount / 1000).toFixed(0)}k</span>
          </div>
          {selected === i && (
            <div className="ml-8 mt-1 space-y-0.5 text-[9px] text-gray-400">
              {m.categories.map((c) => (
                <div key={c.name} className="flex justify-between">
                  <span>{c.name}</span>
                  <span className="font-semibold">{c.val}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function MockupCredits() {
  const [credits, setCredits] = useState([
    { bank: 'BBVA', desc: 'TV Samsung', paid: 5, total: 12, amount: 100, color: '#004481' },
    { bank: 'Santander', desc: 'Prestamo', paid: 8, total: 24, amount: 250, color: '#ec0000' },
  ]);

  const pay = (i) => {
    setCredits((prev) =>
      prev.map((c, idx) =>
        idx === i && c.paid < c.total ? { ...c, paid: c.paid + 1 } : c
      )
    );
  };

  return (
    <div className="mt-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3 space-y-2.5 select-none">
      {credits.map((c, i) => {
        const done = c.paid >= c.total;
        return (
          <div
            key={c.desc}
            onClick={() => pay(i)}
            className={`flex items-center gap-2 rounded-lg p-0.5 transition-colors ${!done ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50' : ''}`}
            title={!done ? 'Click para pagar cuota' : ''}
          >
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center text-[8px] font-bold text-white"
              style={{ backgroundColor: c.color }}
            >
              {c.bank.slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300 truncate">{c.desc}</div>
              <div className={`text-[9px] ${done ? 'text-emerald-500 font-semibold' : 'text-gray-400'}`}>
                {done ? 'Pagado!' : `Cuota ${c.paid}/${c.total}`}
              </div>
            </div>
            <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400">${c.amount}k</span>
          </div>
        );
      })}
    </div>
  );
}

function MockupScan() {
  const [step, setStep] = useState(0);

  const startScan = () => {
    if (step !== 0) return;
    setStep(1);
    setTimeout(() => setStep(2), 1500);
  };

  const reset = () => setStep(0);

  return (
    <div className="mt-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3 select-none">
      {step === 0 && (
        <div
          onClick={startScan}
          className="cursor-pointer flex flex-col items-center gap-2 py-3 border-2 border-dashed border-brand-300 dark:border-brand-500/40 rounded-xl hover:bg-brand-50/50 dark:hover:bg-brand-500/5 transition-colors"
        >
          <div className="w-9 h-9 rounded-xl bg-brand-100 dark:bg-brand-500/20 flex items-center justify-center text-lg">
            📸
          </div>
          <span className="text-[10px] font-semibold text-brand-600 dark:text-brand-400">
            Escanear boleta
          </span>
        </div>
      )}
      {step === 1 && (
        <div className="flex flex-col items-center gap-2 py-3">
          <div className="w-9 h-9 rounded-xl bg-brand-100 dark:bg-brand-500/20 flex items-center justify-center animate-pulse text-lg">
            🔍
          </div>
          <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400">
            Leyendo boleta...
          </span>
          <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full bg-brand-500 rounded-full animate-[scan-progress_1.5s_ease-in-out_forwards]" />
          </div>
          <style>{`
            @keyframes scan-progress {
              0% { width: 0%; }
              100% { width: 100%; }
            }
          `}</style>
        </div>
      )}
      {step === 2 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 rounded-full">
              Confianza 87%
            </span>
            <span className="text-[10px] text-gray-400 ml-auto">Auto-detectado</span>
          </div>
          <div className="space-y-1.5">
            {[
              { label: 'Tienda', value: 'LIDER' },
              { label: 'Total', value: '$85,500' },
              { label: 'Categoria', value: '🍽️ Alimentacion' },
              { label: 'Fecha', value: '21/02/2026' },
            ].map((field) => (
              <div key={field.label} className="flex justify-between text-[10px]">
                <span className="text-gray-400">{field.label}</span>
                <span className="font-semibold text-gray-700 dark:text-gray-300">{field.value}</span>
              </div>
            ))}
          </div>
          <button
            onClick={reset}
            className="w-full text-[9px] font-semibold text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-500/10 rounded-lg py-1.5 hover:bg-brand-100 dark:hover:bg-brand-500/20 transition-colors mt-1"
          >
            Escanear otra
          </button>
        </div>
      )}
    </div>
  );
}

function MockupBusiness() {
  const [selected, setSelected] = useState(null);

  const businesses = [
    { icon: '💼', name: 'Giraffos', currency: 'CLP', income: '$2.8M', expenses: '$1.2M', balance: '$1.6M', color: 'bg-blue-500', projects: 3 },
    { icon: '🚗', name: 'Autos USA', currency: 'USD', income: '$12,500', expenses: '$8,200', balance: '$4,300', color: 'bg-emerald-500', projects: 1 },
  ];

  return (
    <div className="mt-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3 space-y-2 select-none">
      {businesses.map((b, i) => (
        <div key={b.name}>
          <div
            onClick={() => setSelected(selected === i ? null : i)}
            className="flex items-center gap-2 cursor-pointer rounded-lg p-1 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
          >
            <div className={`w-7 h-7 rounded-lg ${b.color} flex items-center justify-center text-xs`}>
              {b.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">{b.name}</div>
              <div className="text-[9px] text-gray-400">{b.currency} · {b.projects} proyecto{b.projects > 1 ? 's' : ''}</div>
            </div>
            <span className="text-[10px] font-bold text-emerald-500">{b.balance}</span>
          </div>
          {selected === i && (
            <div className="ml-9 mt-1 space-y-1 text-[9px]">
              <div className="flex justify-between">
                <span className="text-gray-400">Ingresos</span>
                <span className="font-semibold text-emerald-500">{b.income}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Gastos</span>
                <span className="font-semibold text-red-400">{b.expenses}</span>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function MockupProperties() {
  const [selected, setSelected] = useState(null);

  const properties = [
    { icon: '🏠', name: 'Depto Las Condes', status: 'Arrendado', tenant: 'Juan P.', rent: '$650,000', color: 'bg-violet-500' },
    { icon: '🏢', name: 'Oficina Providencia', status: 'Disponible', tenant: null, rent: '$480,000', color: 'bg-amber-500' },
  ];

  return (
    <div className="mt-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3 space-y-2 select-none">
      {properties.map((p, i) => (
        <div key={p.name}>
          <div
            onClick={() => setSelected(selected === i ? null : i)}
            className="flex items-center gap-2 cursor-pointer rounded-lg p-1 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
          >
            <div className={`w-7 h-7 rounded-lg ${p.color} flex items-center justify-center text-xs`}>
              {p.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">{p.name}</div>
              <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                p.status === 'Arrendado'
                  ? 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                  : 'bg-amber-100 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400'
              }`}>
                {p.status}
              </span>
            </div>
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400">{p.rent}</span>
          </div>
          {selected === i && p.tenant && (
            <div className="ml-9 mt-1 space-y-1 text-[9px]">
              <div className="flex justify-between">
                <span className="text-gray-400">Arrendatario</span>
                <span className="font-semibold text-gray-600 dark:text-gray-300">{p.tenant}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Arriendo</span>
                <span className="font-semibold text-emerald-500">{p.rent}/mes</span>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function MockupProjects() {
  const [projects, setProjects] = useState([
    { icon: '🎨', name: 'Rediseño web', client: 'ClienteX', budget: 2000, collected: 1400, status: 'active', color: 'bg-cyan-500' },
    { icon: '📱', name: 'App móvil', client: 'StartupY', budget: 5000, collected: 5000, status: 'completed', color: 'bg-emerald-500' },
  ]);

  const collect = (i) => {
    setProjects((prev) =>
      prev.map((p, idx) =>
        idx === i && p.collected < p.budget
          ? { ...p, collected: Math.min(p.collected + 300, p.budget) }
          : p
      )
    );
  };

  return (
    <div className="mt-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3 space-y-2.5 select-none">
      {projects.map((p, i) => {
        const pct = Math.round((p.collected / p.budget) * 100);
        const done = p.collected >= p.budget;
        return (
          <div
            key={p.name}
            onClick={() => !done && collect(i)}
            className={`${!done ? 'cursor-pointer' : ''}`}
            title={!done ? 'Click para cobrar hito' : ''}
          >
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-6 h-6 rounded-lg ${p.color} flex items-center justify-center text-[10px]`}>
                {p.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">{p.name}</div>
                <div className="text-[9px] text-gray-400">{p.client}</div>
              </div>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                done
                  ? 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                  : 'bg-blue-100 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400'
              }`}>
                {done ? 'Completado' : 'Activo'}
              </span>
            </div>
            <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${done ? 'bg-emerald-400' : 'bg-cyan-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="flex justify-between text-[9px] text-gray-400 mt-0.5">
              <span>${(p.collected / 1000).toFixed(0)}k / ${(p.budget / 1000).toFixed(0)}k</span>
              <span className={done ? 'text-emerald-500 font-semibold' : ''}>{done ? 'Cobrado!' : `${pct}%`}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const MOCKUP_COMPONENTS = {
  tracking: MockupTracking,
  budgets: MockupBudgets,
  goals: MockupGoals,
  couple: MockupCouple,
  chat: MockupChat,
  family: MockupFamily,
  credits: MockupCredits,
  scan: MockupScan,
  business: MockupBusiness,
  properties: MockupProperties,
  projects: MockupProjects,
};

export default function SolutionSection() {
  const { t } = useTranslation();
  const [visibleCards, setVisibleCards] = useState([]);
  const cardsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.dataset.index);
            setVisibleCards((prev) =>
              prev.includes(index) ? prev : [...prev, index]
            );
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    cardsRef.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-display font-bold text-center text-gray-900 dark:text-white mb-4">
          {t('landing.solution.sectionTitle')}
        </h2>
        <div className="w-16 h-1 bg-gradient-to-r from-brand-500 to-accent-500 mx-auto rounded-full mb-14" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SOLUTION_KEYS.map((key, i) => {
            const Mockup = MOCKUP_COMPONENTS[key];
            return (
              <div
                key={key}
                ref={(el) => (cardsRef.current[i] = el)}
                data-index={i}
                className={`card group hover:border-brand-200 dark:hover:border-brand-800 transition-all duration-500 ${
                  visibleCards.includes(i)
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                  {t(`landing.solution.items.${key}.title`)}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t(`landing.solution.items.${key}.description`)}
                </p>
                {Mockup && <Mockup />}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
